import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { API } from "@/lib/auth";

export default function BridalGalleryAdmin() {
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/admin/bridal/gallery`).then((r) => setImages(r.data.images)).catch(() => toast.error("Could not load gallery"));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    setBusy(true);
    for (const file of Array.from(files || [])) {
      const fd = new FormData(); fd.append("file", file);
      try { await axios.post(`${API}/admin/bridal/gallery`, fd); } catch (e) { toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false); load();
  };
  const update = async (id, body) => {
    await axios.patch(`${API}/admin/bridal/gallery/${id}`, body);
    setImages((imgs) => imgs.map((x) => (x.id === id ? { ...x, ...body } : x)));
  };
  const del = async (id) => {
    if (!window.confirm("Remove this photo from the bridal gallery?")) return;
    await axios.delete(`${API}/admin/bridal/gallery/${id}`); load();
  };
  const reorder = async (i, d) => {
    const a = [...images]; const j = i + d;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]]; setImages(a);
    await axios.patch(`${API}/admin/bridal/gallery/reorder`, { ids: a.map((x) => x.id) });
  };

  if (!images) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  return (
    <div data-testid="bridal-gallery-admin" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[hsl(var(--onyx)/0.6)]">
          {images.length ? `${images.length} photo(s) live on the Bridal page.` : "No photos yet — the page shows labelled placeholders until you upload your own bridal work."}
        </p>
        <label data-testid="bridal-gallery-upload" className="btn-pill btn-primary cursor-pointer">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload photos
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {images.map((img, i) => (
          <div key={img.id} data-testid={`bridal-gallery-admin-item-${i}`} className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-2 space-y-2">
            <img src={img.url} alt="" className="w-full aspect-[4/5] object-cover rounded-xl" />
            <input defaultValue={img.caption || ""} placeholder="Caption (optional)" onBlur={(e) => e.target.value !== (img.caption || "") && update(img.id, { caption: e.target.value })}
              data-testid={`bridal-gallery-caption-${i}`} className="w-full rounded-lg border px-2 py-1.5 text-xs" />
            <div className="flex items-center justify-between gap-1">
              <select value={img.orientation || "portrait"} onChange={(e) => update(img.id, { orientation: e.target.value })} className="rounded-lg border px-1.5 py-1 text-xs">
                <option value="portrait">Portrait</option><option value="landscape">Landscape</option><option value="square">Square</option>
              </select>
              <div className="flex gap-1">
                <button aria-label="Move earlier" onClick={() => reorder(i, -1)} className="p-1.5 rounded-full border"><ArrowUp size={11} /></button>
                <button aria-label="Move later" onClick={() => reorder(i, 1)} className="p-1.5 rounded-full border"><ArrowDown size={11} /></button>
                <button aria-label="Delete" data-testid={`bridal-gallery-delete-${i}`} onClick={() => del(img.id)} className="p-1.5 rounded-full border text-red-600"><Trash2 size={11} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
